import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { listAnnouncements, type Announcement } from "./api";

type AnnouncementsContextValue = {
  announcements: Announcement[];
  // Keyed lookup of the latest known version of each announcement - lets a
  // ribbon that only has the copy embedded in a product fetched minutes ago
  // pick up an edit made just now through AnnouncementsModal or
  // AnnouncementPicker, without refetching the product itself.
  byId: Map<string, Announcement>;
  loading: boolean;
  upsert: (announcement: Announcement) => void;
  remove: (id: string) => void;
  // Replaces the whole list - used to apply (and roll back) a reorder.
  replaceAll: (announcements: Announcement[]) => void;
};

const AnnouncementsContext = createContext<AnnouncementsContextValue | null>(null);

// One shared, app-wide copy of the announcements list instead of every
// picker/modal fetching and holding its own. Whoever creates or edits an
// announcement calls `upsert`/`remove` here, so every other consumer -
// including a ribbon on a product page that never re-fetches the product -
// re-renders with the new message/colors immediately.
export function AnnouncementsProvider({ children }: { children: ReactNode }) {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listAnnouncements()
      .then(setAnnouncements)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const upsert = useCallback((announcement: Announcement) => {
    setAnnouncements(prev => {
      const index = prev.findIndex(a => a.id === announcement.id);
      if (index === -1) return [...prev, announcement];
      const next = prev.slice();
      next[index] = announcement;
      return next;
    });
  }, []);

  const remove = useCallback((id: string) => {
    setAnnouncements(prev => prev.filter(a => a.id !== id));
  }, []);

  const replaceAll = useCallback((next: Announcement[]) => setAnnouncements(next), []);

  const byId = useMemo(() => new Map(announcements.map(a => [a.id, a])), [announcements]);

  const value = useMemo(
    () => ({ announcements, byId, loading, upsert, remove, replaceAll }),
    [announcements, byId, loading, upsert, remove, replaceAll]
  );

  return <AnnouncementsContext.Provider value={value}>{children}</AnnouncementsContext.Provider>;
}

export function useAnnouncements() {
  const ctx = useContext(AnnouncementsContext);
  if (!ctx) throw new Error("useAnnouncements must be used within an AnnouncementsProvider");
  return ctx;
}
